/* eslint-disable react/prop-types */
import { useEffect, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  InputBase,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import MinimizeIcon from '@mui/icons-material/Remove';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import UrlPreviewCard from './UrlPreviewCard';
import VoiceInput from './VoiceInput';
import FilePreview from './FilePreview';
import ChatAttachment from './ChatAttachment';

const NAVY = '#1a1a2e';
const NAVY_DARK = '#0f3460';
const NAVY_GRADIENT = 'linear-gradient(to right, #1a1a2e, #16213e)';
const CHAT_BG = '#f8f9fc';

const LOGO_SRC = '/logo/AIRSTUDIO_Logo_Blue.png';

const linkifyText = (text) => {
  const parts = String(text || '').split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, index) => {
    if (/^https?:\/\//.test(part)) {
      const url = part.replace(/[.,;:!?)]+$/, '');
      const trailing = part.slice(url.length);
      return (
        <span key={`${part}-${index}`}>
          <Typography
            component="a"
            href={url}
            target="_blank"
            rel="noreferrer"
            variant="body2"
            sx={{
              display: 'block',
              color: NAVY_DARK,
              fontWeight: 600,
              wordBreak: 'break-all',
              textDecoration: 'underline',
              fontSize: 14,
              lineHeight: 1.55,
              my: 0.5,
            }}
          >
            {url}
          </Typography>
          {trailing ? (
            <Typography component="span" variant="body2" sx={{ fontSize: 14 }}>
              {trailing}
            </Typography>
          ) : null}
        </span>
      );
    }
    return (
      <Typography
        key={`t-${index}`}
        component="span"
        variant="body2"
        sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55, fontSize: 14 }}
      >
        {part}
      </Typography>
    );
  });
};

function TypingIndicator() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
      <Box
        sx={{
          display: 'flex',
          gap: 0.6,
          px: 1.5,
          py: 1.25,
          borderRadius: '999px',
          border: '1px solid',
          borderColor: 'grey.200',
          bgcolor: 'white',
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'grey.400',
              animation: 'chatBounce 1.2s infinite',
              animationDelay: `${i * 0.15}s`,
              '@keyframes chatBounce': {
                '0%, 80%, 100%': { transform: 'scale(0.7)', opacity: 0.45 },
                '40%': { transform: 'scale(1)', opacity: 1 },
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

function BotAvatar() {
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        flexShrink: 0,
        borderRadius: 1.5,
        bgcolor: 'white',
        p: 0.5,
        boxShadow: '0 0 0 1px rgba(0,0,0,0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        component="img"
        src={LOGO_SRC}
        alt="AIRStudio"
        sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
        onError={(e) => {
          e.currentTarget.style.display = 'none';
        }}
      />
    </Box>
  );
}

function MessageBubble({ message }) {
  const isBot = message.role === 'bot';
  const urlPreview = message.metadata?.type === 'url_preview' ? message.metadata.data : null;
  const attachments = message.attachments || [];
  const fileUrls = message.fileUrls || attachments.map((item) => item.url).filter(Boolean);

  if (isBot) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ maxWidth: '88%' }}>
          <BotAvatar />
          <Box
            sx={{
              px: 1.75,
              py: 1.25,
              borderRadius: '16px 16px 16px 4px',
              border: '1px solid',
              borderColor: 'grey.200',
              bgcolor: 'white',
              color: 'grey.800',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            }}
          >
            <Box sx={{ '& strong': { fontWeight: 700 } }}>
              {linkifyText(message.content.replace(/\*\*(.*?)\*\*/g, '$1'))}
            </Box>
            {urlPreview && <UrlPreviewCard data={urlPreview} />}
          </Box>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
      <Box
        sx={{
          maxWidth: '88%',
          px: 1.75,
          py: 1.25,
          borderRadius: '16px 16px 4px 16px',
          background: `linear-gradient(to bottom right, ${NAVY}, ${NAVY_DARK})`,
          color: 'white',
          boxShadow: '0 2px 8px rgba(15, 52, 96, 0.25)',
        }}
      >
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55, fontSize: 14 }}>
          {message.content}
        </Typography>
        {(attachments.length ? attachments : fileUrls.map((url) => ({ url }))).map((item, index) => (
          <ChatAttachment key={`${item.url}-${index}`} url={item.url} name={item.name} />
        ))}
      </Box>
    </Box>
  );
}

export default function SellerChatPanel({ messages, isTyping, pageContext, onSend, onClose, onClear }) {
  const [input, setInput] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [voiceNotice, setVoiceNotice] = useState('');
  const bottomRef = useRef(null);
  const listRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, isTyping, selectedFiles]);

  const submit = () => {
    if (!input.trim() && !selectedFiles.length) return;
    onSend(input.trim(), selectedFiles);
    setInput('');
    setSelectedFiles([]);
    setVoiceNotice('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const onFilesSelected = (event) => {
    const picked = Array.from(event.target.files || []);
    if (!picked.length) return;
    setSelectedFiles((prev) => [...prev, ...picked]);
    event.target.value = '';
  };

  const onVoiceTranscript = (text, meta) => {
    if (meta?.error) {
      setVoiceNotice(meta.error);
      return;
    }
    setVoiceNotice('');
    if (text) setInput((prev) => (prev ? `${prev} ${text}` : text));
  };

  return (
    <Paper
      elevation={24}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        width: { xs: 'calc(100vw - 32px)', sm: 400 },
        maxWidth: 400,
        height: { xs: 'min(70vh, 560px)', sm: 600 },
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        overflow: 'hidden',
        zIndex: 1500,
        border: '1px solid',
        borderColor: 'grey.200',
      }}
    >
      {/* Header — matches airstudio-white ChatPanel */}
      <Box
        sx={{
          background: NAVY_GRADIENT,
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: 'white',
              p: 0.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <Box
              component="img"
              src={LOGO_SRC}
              alt="AIRStudio"
              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </Box>
          <Box>
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: 14, letterSpacing: 0.4 }}>
              AIRStudio
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: '#34d399',
                  boxShadow: '0 0 6px #34d399',
                }}
              />
              <Typography sx={{ color: '#bfdbfe', fontSize: 11 }}>
                AI Assistant · Online
              </Typography>
            </Stack>
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.25}>
          <Tooltip title="Clear chat">
            <IconButton size="small" onClick={onClear} sx={{ color: 'rgba(255,255,255,0.75)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Minimize">
            <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.75)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
              <MinimizeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {pageContext && (
        <Box sx={{ px: 2, py: 0.75, bgcolor: 'rgba(15, 52, 96, 0.06)', borderBottom: '1px solid', borderColor: 'grey.100' }}>
          <Typography variant="caption" sx={{ color: NAVY_DARK, fontSize: 11 }}>
            {pageContext}
          </Typography>
        </Box>
      )}

      {/* Messages */}
      <Box
        ref={listRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 1.5,
          py: 2,
          bgcolor: CHAT_BG,
        }}
      >
        {messages.length === 0 && !isTyping && (
          <Box sx={{ textAlign: 'center', mt: 3, px: 2 }}>
            <Typography variant="body2" sx={{ color: 'grey.600', fontSize: 13, lineHeight: 1.6 }}>
              Hi! I help with products, subscriptions, orders, and fixing issues. Store logo and product
              images are different — if something is unclear, I&apos;ll ask before assuming. Attach a file,
              use the mic, or describe your problem.
            </Typography>
          </Box>
        )}
        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} />
        ))}
        {isTyping && <TypingIndicator />}
        <Box ref={bottomRef} />
      </Box>

      {/* Input */}
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'grey.100',
          bgcolor: 'white',
          px: 1.5,
          py: 1.25,
          boxShadow: '0 -2px 12px rgba(0,0,0,0.04)',
        }}
      >
        {selectedFiles.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mb: 1, overflowX: 'auto', pb: 0.5 }}>
            {selectedFiles.map((file, index) => (
              <FilePreview
                key={`${file.name}-${index}`}
                file={file}
                onRemove={() =>
                  setSelectedFiles((prev) => prev.filter((_, itemIndex) => itemIndex !== index))
                }
              />
            ))}
          </Stack>
        )}
        {voiceNotice && (
          <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: 'warning.dark' }}>
            {voiceNotice}
          </Typography>
        )}
        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          accept="image/*,.csv,.xlsx,.xls"
          onChange={onFilesSelected}
        />
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <Tooltip title="Attach image or CSV/Excel">
            <IconButton
              size="small"
              onClick={() => fileInputRef.current?.click()}
              disabled={isTyping}
              sx={{
                flexShrink: 0,
                width: 40,
                height: 40,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                color: 'grey.600',
                '&:hover': { borderColor: NAVY_DARK, color: NAVY_DARK },
              }}
            >
              <AttachFileIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <VoiceInput onTranscript={onVoiceTranscript} disabled={isTyping} />
          <InputBase
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Type or speak your message..."
            multiline
            maxRows={4}
            fullWidth
            sx={{
              fontSize: 14,
              px: 1.5,
              py: 1,
              minHeight: 40,
              bgcolor: CHAT_BG,
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 2,
              '&:focus-within': {
                borderColor: NAVY_DARK,
                bgcolor: 'white',
              },
            }}
          />
          <IconButton
            onClick={submit}
            disabled={!input.trim() && !selectedFiles.length}
            sx={{
              flexShrink: 0,
              width: 40,
              height: 40,
              borderRadius: 2,
              background: `linear-gradient(to bottom right, ${NAVY}, ${NAVY_DARK})`,
              color: 'white',
              '&:hover': { opacity: 0.9 },
              '&.Mui-disabled': { background: 'grey.200', color: 'grey.400' },
            }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>
    </Paper>
  );
}
